import React, { useState, useEffect } from "react";
import {
  TextInput,
  Textarea,
  NumberInput,
  Stack,
  Text,
  Grid,
  Alert,
  InputLabel,
  Box,
  Title,
} from "@mantine/core";
import { DateInput, TimePicker } from "@mantine/dates";

import { RunningMessage, RunningMessageInput } from "@/utils/api";

import {
  AppModal,
  AppIcon,
  RecurrenceSelector,
  ImportanceSelector,
  RecurrenceDaysSelector,
} from "@/components/generic";
import { MessageSourceSelector } from "../MessageSourceSelector";
import { StatusSelector } from "../../StatusSelector";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

export interface RunningMessageFormModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  /** Running message to edit (if null/undefined, it's create mode) */
  runningMessage?: RunningMessage | null;

  /** Default venue ID for new messages */
  //venueId?: string;

  /** Callback when message should be created */
  onCreateMessage?: (
    messageData: RunningMessageInput
  ) => Promise<RunningMessage | null>;

  /** Callback when message should be updated */
  onUpdateMessage?: (
    message: RunningMessage,
    messageData: RunningMessageInput
  ) => Promise<RunningMessage | null>;

  /** Whether create operation is in progress */
  isCreating?: boolean;

  /** Whether update operation is in progress */
  isUpdating?: boolean;

  /** Last error from message operations */
  lastError?: string | null;

  /** Callback to clear errors */
  onClearError?: () => void;
}

interface RunningMessageFormData {
  //venueId: string;
  messageSourceType: string;
  name: string;
  messageText: string;
  scrollSpeed: number | string;
  startDatetime?: string | null | undefined;
  endDatetime?: string | null | undefined;
  startTime?: string | null | undefined;
  endTime?: string | null | undefined;
  recurrencePattern?: string | null | undefined;
  recurrenceDays?: string | null | undefined;
  importance?: number | null | undefined;
  status: number;
}

const initialFormData: RunningMessageFormData = {
  //venueId: "",
  messageSourceType: "local",
  name: "",
  messageText: "",
  scrollSpeed: 30,
  startDatetime: null,
  endDatetime: null,
  startTime: null,
  endTime: null,
  recurrencePattern: "NONE",
  recurrenceDays: null,
  importance: 0, // 0 - regular, 1 - importance
  status: 100,
};

export const RunningMessageFormModal: React.FC<
  RunningMessageFormModalProps
> = ({
  opened,
  onClose,
  runningMessage,
  //venueId,
  onCreateMessage,
  onUpdateMessage,
  isCreating = false,
  isUpdating = false,
  lastError,
  onClearError,
}) => {
  const [formData, setFormData] =
    useState<RunningMessageFormData>(initialFormData);
  const [validationErrors, setValidationErrors] = useState<any>({});

  const isEditMode = Boolean(runningMessage) && Boolean(runningMessage?.id);
  const isSubmitting = isCreating || isUpdating;

  // Reset form when modal opens/closes or message changes
  useEffect(() => {
    if (opened) {
      if (runningMessage) {
        // Edit mode - populate form with message data
        setFormData({
          //venueId: runningMessage.venueId || "",
          messageSourceType: runningMessage.messageSourceType || "local",
          name: runningMessage.name || "",
          messageText: runningMessage.messageText || "",
          scrollSpeed: runningMessage.scrollSpeed || 30,
          startDatetime: runningMessage.startDatetime
            ? dayjs(runningMessage.startDatetime).format("YYYY-MM-DDTHH:mm:00Z")
            : null,
          endDatetime: runningMessage.endDatetime
            ? dayjs(runningMessage.endDatetime).format("YYYY-MM-DDTHH:mm:00Z")
            : null,
          startTime: runningMessage.startTime
            ? dayjs(runningMessage.startTime).format("HH:mm")
            : null,
          endTime: runningMessage.endTime
            ? dayjs(runningMessage.endTime).format("HH:mm")
            : null,
          recurrencePattern: runningMessage.recurrencePattern || "NONE",
          recurrenceDays: runningMessage.recurrenceDays || null,
          importance: runningMessage.importance || 0,
          status: runningMessage.status != null ? runningMessage.status : 100,
        });
      } else {
        // Create mode - reset to initial values with default venue
        setFormData({
          ...initialFormData,
          //venueId: venueId || "",
        });
      }
      setValidationErrors({});
      onClearError?.();
    }
  }, [opened, runningMessage, /*venueId,*/ onClearError]);

  const validateForm = (): boolean => {
    const newErrors: any = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = "Message name is required";
    }

    // if (!formData.venueId.trim()) {
    //   newErrors.venueId = "Venue is required";
    // }

    if (!formData.messageSourceType.trim()) {
      newErrors.messageSourceType = "Message source type is required";
    }

    // Message text validation (only required for local source)
    if (!formData.messageText.trim()) {
      newErrors.messageText = "Message is required";
    }

    // Scroll speed validation
    if (formData.scrollSpeed && Number(formData.scrollSpeed) <= 0) {
      newErrors.scrollSpeed = "Scroll speed must be a positive number";
    }

    // Date range validation
    if (formData.startDatetime && formData.endDatetime) {
      if (formData.startDatetime >= formData.endDatetime) {
        newErrors.endDatetime = "End date must be after start date";
      }
    }

    // Time validation
    if (formData.startTime && formData.endTime) {
      if (formData.startTime >= formData.endTime) {
        newErrors.endTime = "End time must be after start time";
      }
    }

    // Recurrence validation
    if (
      formData.recurrencePattern === "WEEKLY" ||
      formData.recurrencePattern === "MONTHLY"
    ) {
      if (!formData.recurrenceDays || formData.recurrenceDays.trim() === "") {
        newErrors.recurrenceDays =
          "Recurrence days are required for weekly and monthly patterns";
      }
    }

    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    field: keyof RunningMessageFormData,
    value: string | number | Date | null
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    console.log("field", value);

    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev: any) => ({
        ...prev,
        [field]: undefined,
      }));
    }

    // Clear recurrenceDays when pattern changes to NONE
    if (field === "recurrencePattern" && value === "NONE") {
      setFormData((prev) => ({
        ...prev,
        recurrenceDays: null,
      }));
    }
  };

  const handleRecurrenceDaysChange = (values: string[]) => {
    const joinedValues = values.length > 0 ? values.join(",") : null;
    handleInputChange("recurrenceDays", joinedValues);
  };

  // Get the error to display for a field
  const getDisplayError = (
    field: keyof RunningMessageFormData
  ): string | null | number | undefined => {
    return validationErrors[field];
  };

  const handleSubmit = async () => {
    // Clear errors on new submission
    onClearError?.();

    if (!validateForm()) {
      return;
    }

    try {
      // Prepare message data
      const messageData: RunningMessageInput = {
        //venueId: formData.venueId.trim(),
        messageSourceType: formData.messageSourceType.trim(),
        name: formData.name.trim(),
        messageText: formData.messageText.trim(),
        scrollSpeed: formData.scrollSpeed ? Number(formData.scrollSpeed) : null,
        startDatetime: formData.startDatetime
          ? dayjs(formData.startDatetime).utc().format("YYYY-MM-DDTHH:mm:00Z")
          : null,
        endDatetime: formData.endDatetime
          ? dayjs(formData.endDatetime).utc().format("YYYY-MM-DDTHH:mm:00Z")
          : null,
        startTime: formData.startTime
          ? dayjs(`1970-01-01 ${formData.startTime}`)
              .utc()
              .format("YYYY-MM-DDTHH:mm:00Z")
          : null,
        endTime: formData.endTime
          ? dayjs(`1970-01-01 ${formData.endTime}`)
              .utc()
              .format("YYYY-MM-DDTHH:mm:00Z")
          : null,
        recurrencePattern: formData.recurrencePattern,
        recurrenceDays: formData.recurrenceDays || null,
        importance: formData.importance || 0,
        status: formData.status,
      };

      let result: RunningMessage | null = null;

      if (isEditMode && runningMessage && onUpdateMessage) {
        // Update existing message
        result = await onUpdateMessage(runningMessage, messageData);
      } else if (!isEditMode && onCreateMessage) {
        // Create new message
        result = await onCreateMessage(messageData);
      }

      // If successful, close the modal
      if (result) {
        onClose();
      }
    } catch (error) {
      // Error handling is managed by the hook/parent component
      console.error("Form submission error:", error);
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  // Render generic errors at the bottom of the form
  const renderGenericErrors = () => {
    if (!lastError) {
      return null;
    }

    return (
      <Alert
        color="red"
        icon={<AppIcon iconName="AlertCircle" size={16} />}
        title="Error"
      >
        <Text size="sm">{lastError}</Text>
      </Alert>
    );
  };

  const getRecurrenceDaysValue = (): string[] => {
    if (!formData.recurrenceDays) return [];
    return formData.recurrenceDays.split(",").filter(Boolean);
  };

  const shouldShowRecurrenceDays =
    formData.recurrencePattern === "WEEKLY" ||
    formData.recurrencePattern === "MONTHLY";

  return (
    <AppModal
      opened={opened}
      onClose={handleCancel}
      title={isEditMode ? "Edit Running Message" : "Add New Running Message"}
      size="lg"
      loading={isSubmitting}
      actions={[
        {
          label: "Cancel",
          variant: "secondary",
          onClick: handleCancel,
          disabled: isSubmitting,
        },
        {
          label: "Confirm",
          variant: "primary",
          iconName: "Check",
          onClick: handleSubmit,
          inProgress: isSubmitting,
        },
      ]}
    >
      <Stack gap="md">
        {/* Basic Information */}
        {/* <Grid>
          <Grid.Col span={12}>
            <VenueSelector
              label="Venue"
              placeholder="Select venue"
              value={formData.venueId}
              onChange={(venueId: string | null, _?: Venue) =>
                handleInputChange("venueId", venueId)
              }
              error={getDisplayError("venueId")}
              required
              withAsterisk
            />
          </Grid.Col>
        </Grid> */}

        <Grid>
          <Grid.Col span={12}>
            <TextInput
              label="Message Name"
              placeholder="Enter message name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              error={getDisplayError("name")}
              required
              withAsterisk
            />
          </Grid.Col>
        </Grid>

        {/* Message Source */}
        <Grid>
          <Grid.Col span={12}>
            <MessageSourceSelector
              label="Message Source"
              placeholder="Select message source"
              value={formData.messageSourceType}
              onChange={(value: string | null) =>
                handleInputChange("messageSourceType", value)
              }
              error={getDisplayError("messageSourceType")}
              withDescriptions
              required
              withAsterisk
            />
          </Grid.Col>
        </Grid>

        {/* Message Content */}
        <Grid>
          <Grid.Col span={12}>
            <Textarea
              label={
                formData.messageSourceType === "local"
                  ? "Message Text"
                  : formData.messageSourceType === "http"
                  ? "HTTP URL"
                  : "RSS Feed URL"
              }
              placeholder={
                formData.messageSourceType === "local"
                  ? "Enter the message text to display"
                  : formData.messageSourceType === "http"
                  ? "Enter HTTP URL to fetch message from"
                  : "Enter RSS feed URL"
              }
              value={formData.messageText}
              onChange={(e) => handleInputChange("messageText", e.target.value)}
              error={getDisplayError("messageText")}
              rows={formData.messageSourceType === "local" ? 3 : 1}
              required={formData.messageSourceType === "local"}
              withAsterisk={formData.messageSourceType === "local"}
            />
          </Grid.Col>
        </Grid>

        {/* Display Configuration */}
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <NumberInput
              label="Scroll Speed"
              placeholder="Speed multiplier"
              value={formData.scrollSpeed}
              onChange={(value) =>
                handleInputChange("scrollSpeed", value || "")
              }
              error={getDisplayError("scrollSpeed")}
              min={10}
              max={200}
              step={1}
              decimalScale={0}
              allowNegative={false}
              required
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <ImportanceSelector
              label="Message Importance"
              placeholder="Select importance"
              value={formData.importance}
              onChange={(value) => handleInputChange("importance", value || "")}
              error={getDisplayError("importance")}
              required
            />
          </Grid.Col>
        </Grid>

        {/* Schedule Section */}
        <Box>
          <Title order={5} mb="xs">
            Schedule (Optional)
          </Title>

          {/* Date Range */}
          <Grid mb="sm">
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <DateInput
                label="Start Date"
                placeholder="Select start date"
                value={formData.startDatetime}
                onChange={(value) => handleInputChange("startDatetime", value)}
                error={getDisplayError("startDatetime")}
                clearable
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <DateInput
                label="End Date"
                placeholder="Select end date"
                value={formData.endDatetime}
                onChange={(value) => handleInputChange("endDatetime", value)}
                error={getDisplayError("endDatetime")}
                clearable
                minDate={formData.startDatetime || undefined}
              />
            </Grid.Col>
          </Grid>

          {/* Time Range */}
          <Grid mb="sm">
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TimePicker
                label="Start Time"
                format="12h"
                withDropdown
                clearable
                value={formData.startTime || ""}
                onChange={(value) =>
                  handleInputChange("startTime", value || null)
                }
                error={getDisplayError("startTime")}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TimePicker
                label="End Time"
                format="12h"
                withDropdown
                clearable
                value={formData.endTime || ""}
                onChange={(value) =>
                  handleInputChange("endTime", value || null)
                }
                error={getDisplayError("endTime")}
              />
            </Grid.Col>
          </Grid>

          {/* Recurrence Pattern */}
          <Grid>
            <Grid.Col span={12}>
              <RecurrenceSelector
                label="Recurrence Pattern"
                placeholder="Select recurrence"
                value={formData.recurrencePattern}
                onChange={(value) =>
                  handleInputChange("recurrencePattern", value)
                }
                error={getDisplayError("recurrencePattern")}
              />
            </Grid.Col>
          </Grid>

          {/* Recurrence Days */}
          {shouldShowRecurrenceDays && (
            <Grid mt="sm">
              <Grid.Col span={12}>
                <RecurrenceDaysSelector
                  recurrencePattern={formData.recurrencePattern}
                  value={getRecurrenceDaysValue()}
                  onChange={handleRecurrenceDaysChange}
                  error={getDisplayError("recurrenceDays")}
                />
              </Grid.Col>
            </Grid>
          )}
        </Box>

        {/* Status */}
        <Grid>
          <Grid.Col span={12}>
            <Stack gap={0}>
              <InputLabel required>Status</InputLabel>
              <StatusSelector
                value={formData.status}
                onChange={(value: any) => handleInputChange("status", value)}
                error={getDisplayError("status")}
                required
              />
            </Stack>
          </Grid.Col>
        </Grid>

        {/* Generic errors */}
        {renderGenericErrors()}
      </Stack>
    </AppModal>
  );
};
